import { Box, Chip, Divider, FormControl, Link, MenuItem, Select, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DrawerMenu from '../../../components/DrawerMenu';
import { routes } from '../../../utils/constants/routes';
import { formatGuestCountCompact } from '../../../utils/validators/HelperFunctions';

type VenueNode = {
  id: number;
  title: string;
  children?: VenueNode[];
};

type PartOfDayOption = {
  code: string;
  name: string;
};

type StatusStyle = {
  status: string;
  background: string;
  color: string;
  dot: string;
  labelKey: string;
};

const EXCLUDED_STATUSES = new Set(['CANCELLED', 'EXPIRED', 'DELETED', 'BLOCKED', 'UNAVAILABLE', 'HIGH_DEMAND']);

/** Strongest blocking status first. */
const STATUS_STYLES: StatusStyle[] = [
  { status: 'CONFIRMED', background: '#D1FADF', color: '#027A48', dot: '#12B76A', labelKey: 'confirmedText' },
  { status: 'CANCELLATION_REQUEST', background: '#CFF4FC', color: '#0E7490', dot: '#17a2b8', labelKey: 'cancelRequestText' },
  { status: 'ACTUALIZED', background: '#F3E8FF', color: '#6B21A8', dot: '#800080', labelKey: 'actualization_text' },
  { status: 'AWAITING_CONFIRMATION', background: '#F8F6D8', color: '#6F6A16', dot: '#A3A329', labelKey: 'awaitingConfirmationText' },
  { status: 'TENTATIVE', background: '#FEF0C7', color: '#B54708', dot: '#F79009', labelKey: 'tentativeText' },
  { status: 'WAITLISTED', background: '#D1E9FF', color: '#1849A9', dot: '#007bff', labelKey: 'waitListText' },
  { status: 'ENQUIRY', background: '#E8EAF6', color: '#3F51B5', dot: '#5C6BC0', labelKey: 'venue_availability_enquiry' },
];

const EMPTY_STYLE = { background: '#FFFFFF', color: '#98A2B3' };
const LABEL_COLUMN_WIDTH = 280;
/** Same orange the booking diary uses for high-demand days. */
const HIGH_DEMAND_COLOR = '#f85f18';
const HIGH_DEMAND_WASH = '#FFF4ED';
const UNAVAILABLE_MARK = '#98A2B3';

const flattenVenues = (venues: VenueNode[]): { id: number; name: string }[] => {
  const rows: { id: number; name: string }[] = [];
  venues.forEach(venue => {
    rows.push({ id: venue.id, name: venue.title });
    (venue.children ?? []).forEach(child => rows.push({ id: child.id, name: child.title }));
  });
  return rows;
};

const eachInclusiveDay = (startValue: string | Date, endValue: string | Date, visit: (dayKey: string) => void) => {
  let cursor = dayjs(startValue).startOf('day');
  const end = dayjs(endValue).startOf('day');
  if (!cursor.isValid() || !end.isValid()) {
    return;
  }
  while (!cursor.isAfter(end, 'day')) {
    visit(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
};

const overlapsDay = (booking: { start?: string | Date; end?: string | Date }, day: Dayjs) => {
  if (!booking?.start || !booking?.end) {
    return false;
  }
  const start = dayjs(booking.start);
  const end = dayjs(booking.end);
  return start.isBefore(day.endOf('day')) && end.isAfter(day.startOf('day'));
};

const bookingsForSlot = (bookings: any[], venueId: number, partOfDayCode: string, day: Dayjs) =>
  bookings
    .filter(booking => {
      if (!booking?.partOfDay || EXCLUDED_STATUSES.has(booking.status)) {
        return false;
      }
      if (Number(booking.venueId) !== venueId) {
        return false;
      }
      if (String(booking.partOfDay).toUpperCase() !== partOfDayCode.toUpperCase()) {
        return false;
      }
      return overlapsDay(booking, day);
    })
    .sort((left, right) => dayjs(left.start).valueOf() - dayjs(right.start).valueOf());

const cellAvailability = (bookings: any[], venueId: number, partOfDayCode: string, day: Dayjs) => {
  const matches = bookingsForSlot(bookings, venueId, partOfDayCode, day);
  const present = new Set(matches.map(booking => booking.status));
  const statuses = STATUS_STYLES.filter(item => present.has(item.status));

  return {
    count: matches.length,
    style: statuses[0] ?? EMPTY_STYLE,
    statuses,
  };
};

const bookingDateTime = (booking: any) => {
  const start = dayjs(booking.start);
  const end = dayjs(booking.end);
  if (!start.isValid()) {
    return { dateLabel: '', timeLabel: '' };
  }
  const dateLabel = start.format('D MMM YYYY');
  const startTime = start.format('HH:mm');
  const timeLabel = end.isValid() ? `${startTime} - ${end.format('HH:mm')}` : startTime;
  return { dateLabel, timeLabel };
};

const bookingGuestLine = (booking: any) => {
  const expectedCount = booking.expectedGuests ?? booking.numberOfGuests;
  const hasGuestData = booking.guaranteedGuests != null || expectedCount != null;
  if (!hasGuestData) {
    return '';
  }
  return formatGuestCountCompact(booking.guaranteedGuests ?? expectedCount, expectedCount);
};

const SlotBookingCard = ({
  booking,
  strings,
  onOpen,
}: {
  booking: any;
  strings: any;
  onOpen: () => void;
}) => {
  const status = STATUS_STYLES.find(item => item.status === booking.status);
  const eventLine = [booking.eventType, booking.eventSubtype].filter(value => String(value || '').trim()).join(' · ');
  const { dateLabel, timeLabel } = bookingDateTime(booking);
  const guestLine = bookingGuestLine(booking);

  return (
    <Box
      sx={{
        border: '1px solid #EEF2F6',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(16, 24, 40, 0.08)',
        p: 1.5,
        mb: 1.25,
        bgcolor: '#fff',
      }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Link
            component="button"
            underline="always"
            onClick={onOpen}
            sx={{
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.3,
              pr: 1,
              textAlign: 'left',
              cursor: 'pointer',
              background: 'none',
              border: 0,
              p: 0,
              fontFamily: 'inherit',
              color: 'primary.main',
            }}>
            {booking.customerName || '—'}
          </Link>
        {booking.status && (
          <Chip
            size="small"
            label={String(booking.status).replace(/_/g, ' ')}
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'capitalize',
              bgcolor: status?.dot || '#667085',
              color: '#fff',
              flexShrink: 0,
            }}
          />
        )}
      </Box>
      {String(booking.companyName || '').trim() && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{booking.companyName}</Typography>
      )}

      {(eventLine || dateLabel || timeLabel || guestLine) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
            {eventLine && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CelebrationOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }}>{eventLine}</Typography>
              </Box>
            )}
            {(dateLabel || timeLabel) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }}>
                  {[dateLabel, timeLabel].filter(Boolean).join(' | ')}
                </Typography>
              </Box>
            )}
            {guestLine && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GroupsOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }}>
                  {strings.calendar_tooltip_guests} {guestLine}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

type SelectedSlot = {
  venueId: number;
  venueName: string;
  partOfDayCode: string;
  partOfDayName: string;
  day: Dayjs;
};

export const VenueFilterSelect = ({
  venues,
  value,
  onChange,
  allLabel,
}: {
  venues: { id: number; name: string }[];
  value: string;
  onChange: (venueId: string) => void;
  allLabel: string;
}) => (
  <FormControl size="small" fullWidth>
    <Select
      value={value}
      displayEmpty
      onChange={event => onChange(String(event.target.value))}
      renderValue={selected => {
        const venue = venues.find(item => String(item.id) === selected);
        return venue?.name || allLabel;
      }}
      sx={{
        height: 32,
        fontSize: 12,
        fontWeight: 500,
        bgcolor: '#fff',
        '& .MuiSelect-select': { py: 0.5, pr: '28px !important' },
      }}>
      <MenuItem value="">{allLabel}</MenuItem>
      {venues.map(venue => (
        <MenuItem key={venue.id} value={String(venue.id)} sx={{ fontSize: 13 }}>
          {venue.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

interface VenueAvailabilityGridProps {
  monthDate: string | Date;
  venues: VenueNode[];
  partsOfDay: PartOfDayOption[];
  bookings: any[];
  strings: any;
  selectedVenueId: string;
  onSelectedVenueChange: (venueId: string) => void;
  onDateOverlayClick?: (day: Dayjs, kind: 'unavailable' | 'highDemand') => void;
}

const VenueAvailabilityGrid: FC<VenueAvailabilityGridProps> = ({
  monthDate,
  venues,
  partsOfDay,
  bookings,
  strings,
  selectedVenueId,
  onSelectedVenueChange,
  onDateOverlayClick,
}) => {
  const navigate = useNavigate();
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
  const [expandedOverrides, setExpandedOverrides] = useState<Record<number, boolean>>({});
  const days = useMemo(() => {
    const month = dayjs(monthDate);
    const count = month.daysInMonth();
    return Array.from({ length: count }, (_, index) => month.date(index + 1));
  }, [monthDate]);
  const venueRows = useMemo(() => flattenVenues(venues), [venues]);
  const visibleVenueRows = useMemo(
    () => (selectedVenueId ? venueRows.filter(venue => String(venue.id) === selectedVenueId) : venueRows),
    [selectedVenueId, venueRows],
  );
  const venueBookingCounts = useMemo(() => {
    const counts = new Map<number, number>();
    venueRows.forEach(venue => {
      const ids = new Set<string>();
      bookings.forEach(booking => {
        if (!booking?.partOfDay || EXCLUDED_STATUSES.has(booking.status)) {
          return;
        }
        if (Number(booking.venueId) !== venue.id) {
          return;
        }
        if (!days.some(day => overlapsDay(booking, day))) {
          return;
        }
        ids.add(String(booking.id ?? `${booking.start}-${booking.partOfDay}`));
      });
      counts.set(venue.id, ids.size);
    });
    return counts;
  }, [bookings, days, venueRows]);
  const highDemandDays = useMemo(() => {
    const keys = new Set<string>();
    bookings.forEach(booking => {
      if (booking?.status !== 'HIGH_DEMAND' || !booking.start || !booking.end) {
        return;
      }
      eachInclusiveDay(booking.start, booking.end, dayKey => keys.add(dayKey));
    });
    return keys;
  }, [bookings]);
  const unavailableVenueDays = useMemo(() => {
    const keys = new Set<string>();
    bookings.forEach(booking => {
      if (booking?.status !== 'UNAVAILABLE' || booking.venueId == null || !booking.start || !booking.end) {
        return;
      }
      const venueId = Number(booking.venueId);
      eachInclusiveDay(booking.start, booking.end, dayKey => keys.add(`${venueId}:${dayKey}`));
    });
    return keys;
  }, [bookings]);
  const selectedBookings = useMemo(
    () =>
      selectedSlot
        ? bookingsForSlot(bookings, selectedSlot.venueId, selectedSlot.partOfDayCode, selectedSlot.day)
        : [],
    [bookings, selectedSlot],
  );

  useEffect(() => {
    setSelectedSlot(null);
    setExpandedOverrides({});
  }, [monthDate]);

  useEffect(() => {
    if (selectedVenueId && !venueRows.some(venue => String(venue.id) === selectedVenueId)) {
      onSelectedVenueChange('');
    }
  }, [onSelectedVenueChange, selectedVenueId, venueRows]);

  if (!venueRows.length || !partsOfDay.length) {
    return (
      <Typography sx={{ color: '#667085', py: 3, px: 1 }}>{strings.venue_availability_empty}</Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: 560,
          border: '1px solid #E4E7EC',
          borderRadius: '8px',
        }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            minWidth: '100%',
            '& th, & td': {
              borderBottom: '1px solid #E4E7EC',
              borderRight: '1px solid #E4E7EC',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
            },
          }}>
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  background: '#F9FAFB',
                  width: LABEL_COLUMN_WIDTH,
                  minWidth: LABEL_COLUMN_WIDTH,
                  maxWidth: LABEL_COLUMN_WIDTH,
                  textAlign: 'left',
                  px: 0.75,
                  py: 0.75,
                }}>
                <VenueFilterSelect
                  venues={venueRows}
                  value={selectedVenueId}
                  allLabel={strings.venue_availability_all_venues}
                  onChange={venueId => {
                    onSelectedVenueChange(venueId);
                    setSelectedSlot(null);
                  }}
                />
              </Box>
              {days.map(day => {
                const today = day.isSame(dayjs(), 'day');
                const highDemand = highDemandDays.has(day.format('YYYY-MM-DD'));
                return (
                <Box
                  component="th"
                  key={day.format('YYYY-MM-DD')}
                  title={highDemand ? strings.high_demand_header : undefined}
                  onClick={highDemand ? () => onDateOverlayClick?.(day, 'highDemand') : undefined}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: highDemand ? HIGH_DEMAND_COLOR : today ? '#D6E4FF' : '#F9FAFB',
                    color: highDemand ? '#fff' : today ? '#175CD3' : undefined,
                    minWidth: 40,
                    textAlign: 'center',
                    px: 0.5,
                    py: 0.75,
                    fontWeight: today || highDemand ? 700 : 600,
                    cursor: highDemand ? 'pointer' : 'default',
                  }}>
                  <Box>{day.format('D')}</Box>
                  <Box sx={{ fontWeight: today || highDemand ? 700 : 500, color: highDemand ? '#fff' : today ? '#175CD3' : '#667085' }}>{day.format('dd')}</Box>
                </Box>
                );
              })}
            </Box>
          </Box>
          {visibleVenueRows.map(venue => {
            const bookingCount = venueBookingCounts.get(venue.id) ?? 0;
            const expanded = expandedOverrides[venue.id] ?? bookingCount > 0;
            const bookingCountLabel = String(
              strings.formatString(
                bookingCount === 1
                  ? strings.venue_availability_booking_count
                  : strings.venue_availability_bookings_count,
                bookingCount,
              ),
            );
            const toggleVenue = () => {
              setExpandedOverrides(current => ({
                ...current,
                [venue.id]: !expanded,
              }));
            };
            return (
            <Box component="tbody" key={venue.id}>
              <Box component="tr">
                <Box
                  component="td"
                  onClick={toggleVenue}
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    background: '#F2F4F7',
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.75,
                    width: LABEL_COLUMN_WIDTH,
                    minWidth: LABEL_COLUMN_WIDTH,
                    maxWidth: LABEL_COLUMN_WIDTH,
                    cursor: 'pointer',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <KeyboardArrowRightIcon
                      sx={{
                        fontSize: 18,
                        color: '#344054',
                        flexShrink: 0,
                        transform: expanded ? 'rotate(90deg)' : 'none',
                        transition: 'transform 120ms ease',
                      }}
                    />
                    <Box
                      component="span"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {venue.name}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#344054',
                        bgcolor: '#FFFFFF',
                        border: '1px solid #D0D5DD',
                        borderRadius: '12px',
                        px: 0.75,
                        py: '1px',
                        lineHeight: 1.4,
                      }}>
                      {bookingCountLabel}
                    </Box>
                  </Box>
                </Box>
                {days.map(day => {
                  const today = day.isSame(dayjs(), 'day');
                  const highDemand = highDemandDays.has(day.format('YYYY-MM-DD'));
                  const unavailable = unavailableVenueDays.has(`${venue.id}:${day.format('YYYY-MM-DD')}`);
                  const overlayKind = unavailable ? 'unavailable' : highDemand ? 'highDemand' : null;
                  return (
                  <Box
                    component="td"
                    key={day.format('YYYY-MM-DD')}
                    title={
                      unavailable
                        ? strings.venue_availability_unavailable
                        : highDemand
                          ? strings.high_demand_header
                          : undefined
                    }
                    onClick={overlayKind ? () => onDateOverlayClick?.(day, overlayKind) : toggleVenue}
                    sx={{
                      background: highDemand ? HIGH_DEMAND_WASH : today ? '#F4F8FF' : '#F2F4F7',
                      minWidth: 40,
                      cursor: 'pointer',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}>
                    {unavailable && <BlockIcon sx={{ fontSize: 14, color: UNAVAILABLE_MARK, verticalAlign: 'middle' }} />}
                  </Box>
                  );
                })}
              </Box>
              {expanded && partsOfDay.map(partOfDay => (
                <Box component="tr" key={`${venue.id}-${partOfDay.code}`}>
                  <Box
                    component="td"
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      background: '#FFFFFF',
                      px: 1.25,
                      pl: 2.5,
                      py: 0.75,
                      color: '#344054',
                      width: LABEL_COLUMN_WIDTH,
                      minWidth: LABEL_COLUMN_WIDTH,
                      maxWidth: LABEL_COLUMN_WIDTH,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {partOfDay.name}
                  </Box>
                  {days.map(day => {
                    const today = day.isSame(dayjs(), 'day');
                    const highDemand = highDemandDays.has(day.format('YYYY-MM-DD'));
                    const unavailable = unavailableVenueDays.has(`${venue.id}:${day.format('YYYY-MM-DD')}`);
                    const cell = cellAvailability(bookings, venue.id, partOfDay.code, day);
                    const hasBookings = cell.count > 0;
                    const statusLabels = cell.statuses.map(item => strings[item.labelKey]).join(', ');
                    const bookingLabel = hasBookings
                      ? `${venue.name}, ${partOfDay.name}, ${day.format('D MMM')}: ${cell.count} (${statusLabels})`
                      : '';
                    const label = [unavailable ? strings.venue_availability_unavailable : '', bookingLabel]
                      .filter(Boolean)
                      .join(' — ');
                    const emptyBackground = highDemand ? HIGH_DEMAND_WASH : cell.style.background;
                    return (
                      <Box
                        component="td"
                        key={day.format('YYYY-MM-DD')}
                        title={label || (highDemand ? strings.high_demand_header : undefined)}
                        onClick={
                          unavailable
                            ? () => onDateOverlayClick?.(day, 'unavailable')
                            : hasBookings
                              ? () =>
                                  setSelectedSlot({
                                    venueId: venue.id,
                                    venueName: venue.name,
                                    partOfDayCode: partOfDay.code,
                                    partOfDayName: partOfDay.name,
                                    day,
                                  })
                              : highDemand
                                ? () => onDateOverlayClick?.(day, 'highDemand')
                                : undefined
                        }
                        sx={{
                          textAlign: 'center',
                          fontWeight: 700,
                          backgroundColor: hasBookings ? cell.style.background : emptyBackground,
                          backgroundImage: today
                            ? 'linear-gradient(rgba(23, 92, 211, 0.08), rgba(23, 92, 211, 0.08))'
                            : undefined,
                          color: cell.style.color,
                          minWidth: 44,
                          px: 0.25,
                          py: 0.5,
                          lineHeight: 1.1,
                          cursor: unavailable || hasBookings || highDemand ? 'pointer' : 'default',
                          verticalAlign: 'middle',
                        }}>
                        {unavailable ? (
                          <BlockIcon sx={{ fontSize: 16, color: UNAVAILABLE_MARK, verticalAlign: 'middle' }} />
                        ) : (
                          <>
                            {cell.count > 0 && <Box>{cell.count}</Box>}
                            {cell.statuses.length > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '2px', mt: '3px' }}>
                                {cell.statuses.map(item => (
                                  <Box
                                    key={item.status}
                                    title={strings[item.labelKey]}
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: item.dot,
                                      flexShrink: 0,
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
            );
          })}
        </Box>
      </Box>
      <DrawerMenu
        open={selectedSlot != null}
        closeButton
        flexColumn
        overlapHeader
        width={420}
        handleClose={() => setSelectedSlot(null)}
        title={selectedSlot ? selectedSlot.day.format('D MMM YYYY') : ''}>
        {selectedSlot && (
          <Box sx={{ width: '100%', px: 0.5 }}>
            <Typography sx={{ fontWeight: 700 }}>{selectedSlot.venueName}</Typography>
            <Typography sx={{ color: '#667085', fontSize: '0.8125rem', mb: 1.5 }}>
              {selectedSlot.partOfDayName}
            </Typography>
            {selectedBookings.length === 0 ? (
              <Typography sx={{ color: '#667085', fontSize: '0.875rem' }}>
                {strings.venue_availability_no_bookings}
              </Typography>
            ) : (
              selectedBookings.map(booking => (
                <SlotBookingCard
                  key={`${booking.id}-${booking.start}`}
                  booking={booking}
                  strings={strings}
                  onOpen={() => navigate(`${routes.bookings}/${booking.id}`)}
                />
              ))
            )}
          </Box>
        )}
      </DrawerMenu>
    </Box>
  );
};

export default VenueAvailabilityGrid;
